import { useEffect, useMemo } from 'react';
import { styled } from 'goober';

import { QueryKey } from '@/config/query-client';
import type { IOption } from '@/types';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';

import { useParams } from 'react-router-dom';
import {
  exploreResultsSortState,
  exploreViewingRoundNumberState,
  isExploreRoundCreatingState,
} from './explore.state';
import { useExploreForm } from './form/use-explore-form';
import { useRequestHistorySuggestions } from './history/use-request-history-suggestions';
import {
  mapRoundCompaniesToExampleCompanies,
  mapRoundLocationsToOptions,
} from './refine/build-create-explore-round-payload';
import { ExploreErrorView } from './results/explore-error-view';
import { ExploreNoResultsView } from './results/explore-no-results-view';
import { ExploreResults } from './results/explore-results';
import { ExploreLoadingView } from './results/loading/explore-loading-view';
import { useExploreNavigationHistory } from './use-explore-navigation-history';
import { useExploreSuggestions } from './use-explore-suggestions';
import { sortByShortDescriptionAndRelevancy } from './sort-by-short-description-and-result-number';
import { useAtom, useAtomValue } from 'jotai';

export const ExploreResultsPage = () => {
  const { requestId } = useParams();
  const { isFromHistory } = useExploreNavigationHistory();
  const [viewingRoundNumber, setViewingRoundNumber] = useAtom(
    exploreViewingRoundNumberState,
  );
  const {
    data: requestHistoryData,
    isError: isErrorRequestHistoryData,
    isLoading: isLoadingRequestHistoryData,
  } = useRequestHistorySuggestions(requestId, Boolean(requestId));
  const {
    data: generatedSuggestions,
    isError: isErrorGeneratedSuggestions,
    isLoading: isLoadingGeneratedSuggestions,
  } = useExploreSuggestions();
  const {
    description: formDescription,
    exampleCompanies: formExampleCompanies,
    locations: formLocations,
  } = useExploreForm();
  const sort = useAtomValue(exploreResultsSortState);
  const isCreatingRound = useAtomValue(isExploreRoundCreatingState);

  useEffect(() => {
    if (!requestHistoryData) {
      return;
    }
    setViewingRoundNumber(current => {
      if (
        current !== null &&
        requestHistoryData.rounds.some(round => round.roundNumber === current)
      ) {
        return current;
      }
      return requestHistoryData.activeRoundNumber;
    });
  }, [requestHistoryData, setViewingRoundNumber]);

  useEffect(() => {
    return () => {
      setViewingRoundNumber(null);
    };
  }, [requestId, setViewingRoundNumber]);

  const selectedRound = useMemo(() => {
    if (!requestHistoryData || viewingRoundNumber === null) {
      return null;
    }
    return (
      requestHistoryData.rounds.find(
        round => round.roundNumber === viewingRoundNumber,
      ) ?? null
    );
  }, [requestHistoryData, viewingRoundNumber]);

  const inputData = useMemo(() => {
    if (selectedRound) {
      return {
        description: selectedRound.prompt,
        exampleCompanies: mapRoundCompaniesToExampleCompanies(
          selectedRound.companies,
        ),
        locations: mapRoundLocationsToOptions(selectedRound),
      };
    }

    if (!requestHistoryData) {
      return {
        description: formDescription,
        exampleCompanies: formExampleCompanies,
        locations: formLocations,
      };
    }

    const locations: IOption[] = [
      ...(requestHistoryData.locationContinent?.map(continent => ({
        label: continent.shortDescription,
        value: JSON.stringify({ id: continent.id, type: 'continent' }),
      })) ?? []),
      ...(requestHistoryData.locationCountry?.map(country => ({
        label: country.shortDescription,
        value: JSON.stringify({ id: country.id, type: 'country' }),
      })) ?? []),
    ];

    const exampleCompanies = mapRoundCompaniesToExampleCompanies(
      requestHistoryData.companies,
    );

    return {
      description: requestHistoryData.requestStatement ?? '',
      exampleCompanies,
      locations,
    };
  }, [
    selectedRound,
    requestHistoryData,
    formDescription,
    formExampleCompanies,
    formLocations,
  ]);

  const suggestionData = useMemo(() => {
    if (selectedRound) {
      return sortByShortDescriptionAndRelevancy([...selectedRound.result]);
    }

    if (requestHistoryData?.result) {
      return requestHistoryData.result;
    }

    if (!isFromHistory) {
      return generatedSuggestions?.results ?? [];
    }

    return [];
  }, [
    selectedRound,
    requestHistoryData?.result,
    isFromHistory,
    generatedSuggestions?.results,
  ]);

  const dataQueryKey = requestHistoryData
    ? QueryKey.ExploreRequestHistorySuggestions
    : QueryKey.ExploreSuggestions;

  const isWaitingForInitialSuggestions =
    !isFromHistory && !requestHistoryData && isLoadingGeneratedSuggestions;

  const isLoading =
    isWaitingForInitialSuggestions ||
    (Boolean(requestId) &&
      isLoadingRequestHistoryData &&
      !generatedSuggestions?.results?.length);

  const isError =
    (Boolean(requestId) &&
      isErrorRequestHistoryData &&
      !suggestionData.length) ||
    (!requestHistoryData && !isFromHistory && isErrorGeneratedSuggestions);

  const latestRoundNumber =
    requestHistoryData?.roundCount ??
    requestHistoryData?.activeRoundNumber ??
    1;
  const resolvedViewingRoundNumber =
    viewingRoundNumber ??
    requestHistoryData?.activeRoundNumber ??
    latestRoundNumber;

  return (
    <WithHtmlSavvyTitle title="Explore">
      <Page name="explore-results-page" direction="column">
        {isLoading || isCreatingRound ? (
          <ExploreLoadingView />
        ) : isError ? (
          <ExploreErrorView />
        ) : suggestionData?.length && requestId ? (
          <ExploreResults
            inputData={inputData}
            suggestionData={suggestionData}
            key={sort.direction}
            dataQueryKey={dataQueryKey}
            requestId={requestId}
            searchTitle={requestHistoryData?.title}
            rounds={requestHistoryData?.rounds ?? []}
            viewingRoundNumber={resolvedViewingRoundNumber}
            latestRoundNumber={latestRoundNumber}
            roundCount={requestHistoryData?.roundCount ?? latestRoundNumber}
            onViewingRoundChange={setViewingRoundNumber}
          />
        ) : suggestionData?.length === 0 && requestId ? (
          <ExploreNoResultsView />
        ) : (
          <ExploreLoadingView />
        )}
      </Page>
    </WithHtmlSavvyTitle>
  );
};

const Page = styled(Flexbox)`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.palettes.component.explore.page.background};
`;
